var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import client from 'api/environment';
import ADD_CHANNEL from 'api/mutations/ADD_CHANNEL';
import ADD_CONVERSATION from 'api/mutations/ADD_CONVERSATION';
import ADD_CONVERSATION_MEMBER from 'api/mutations/ADD_CONVERSATION_MEMBER';
import ADD_CONVERSATION_MESSAGE from 'api/mutations/ADD_CONVERSATION_MESSAGE';
import REMOVE_CHANNEL from 'api/mutations/REMOVE_CHANNEL';
import REMOVE_CONVERSATION_MEMBER from 'api/mutations/REMOVE_CONVERSATION_MEMBER';
import RESET_CONVERSATION_UNREAD_COUNT from 'api/mutations/RESET_CONVERSATION_UNREAD_COUNT';
import GET_CHANNELS from 'api/queries/GET_CHANNELS';
import GET_CONVERSATION from 'api/queries/GET_CONVERSATION';
import GET_DIRECT_CONVERSATIONS from 'api/queries/GET_DIRECT_CONVERSATIONS';
import GET_SPECIFICATION_AND_FOLDER_CONVERSATIONS from 'api/queries/GET_SPECIFICATION_AND_FOLDER_CONVERSATIONS';
import GET_USER_UNREAD_COUNTS from 'api/queries/GET_USER_UNREAD_COUNTS';
import withAttachments from 'utils/graphql';
import { clearUnreadCounts, loadTotalUnreadCount, loadUnreadCounts, saveTotalUnreadCount, saveUnreadCounts, updateUnreadCountForConversation, } from 'utils/unreadCountPersistence';
const initialState = {
    channels: [],
    privateMessages: [],
    specificationAndFolderConversations: [],
    conversation: null,
    status: {
        fetchChannels: 'idle',
        fetchPrivateMessages: 'idle',
        fetchSpecificationAndFolderConversations: 'idle',
        fetchConversation: 'idle',
        addChannel: 'idle',
        updateChannel: 'idle',
        removeChannel: 'idle',
        addDirectConversation: 'idle',
        fetchUserUnreadCounts: 'idle',
        resetConversationUnreadCount: 'idle',
        addMember: 'idle',
        removeMember: 'idle',
    },
    errors: {
        fetchChannels: null,
        fetchPrivateMessages: null,
        fetchSpecificationAndFolderConversations: null,
        fetchConversation: null,
        addChannel: null,
        updateChannel: null,
        removeChannel: null,
        addDirectConversation: null,
        fetchUserUnreadCounts: null,
        resetConversationUnreadCount: null,
        addMember: null,
        removeMember: null,
    },
    refetchFlags: {
        fetchChannels: false,
        fetchPrivateMessages: false,
        fetchSpecificationAndFolderConversations: false,
        fetchConversation: false,
        addChannel: false,
        updateChannel: false,
        removeChannel: false,
        addDirectConversation: false,
        fetchUserUnreadCounts: false,
        resetConversationUnreadCount: false,
    },
    unreadCount: loadTotalUnreadCount(),
    unreadCountByConversation: loadUnreadCounts(),
};
export const fetchChannels = createAsyncThunk('inbox/fetchChannels', (_1, _a) => __awaiter(void 0, [_1, _a], void 0, function* (_, { rejectWithValue }) {
    try {
        const channelsData = yield client.query({
            query: GET_CHANNELS,
            fetchPolicy: 'network-only',
        });
        return channelsData.data.getChannels;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchPrivateMessages = createAsyncThunk('inbox/fetchPrivateMessages', (_1, _a) => __awaiter(void 0, [_1, _a], void 0, function* (_, { rejectWithValue }) {
    try {
        const responseData = yield client.query({
            query: GET_DIRECT_CONVERSATIONS,
            fetchPolicy: 'network-only',
        });
        return responseData.data.getDirectConversations;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchSpecificationAndFolderConversations = createAsyncThunk('inbox/fetchSpecificationAndFolderConversations', (_1, _a) => __awaiter(void 0, [_1, _a], void 0, function* (_, { rejectWithValue }) {
    try {
        const responseData = yield client.query({
            query: GET_SPECIFICATION_AND_FOLDER_CONVERSATIONS,
            fetchPolicy: 'network-only',
        });
        return responseData.data.getSpecificationAndFolderConversations;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchUserUnreadCounts = createAsyncThunk('inbox/fetchUserUnreadCounts', (_1, _a) => __awaiter(void 0, [_1, _a], void 0, function* (_, { rejectWithValue }) {
    try {
        const responseData = yield client.query({
            query: GET_USER_UNREAD_COUNTS,
            fetchPolicy: 'network-only',
        });
        return responseData.data.getUserUnreadCounts;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const resetConversationUnreadCount = createAsyncThunk('inbox/resetConversationUnreadCount', (conversationId_1, _a) => __awaiter(void 0, [conversationId_1, _a], void 0, function* (conversationId, { rejectWithValue }) {
    try {
        const responseData = yield client.mutate({
            mutation: RESET_CONVERSATION_UNREAD_COUNT,
            variables: { conversationId },
            context: { silentReadOnly: true },
        });
        return { conversationId, success: responseData.data.resetConversationUnreadCount };
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchConversation = createAsyncThunk('inbox/fetchConversation', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ url }, { rejectWithValue }) {
    try {
        const responseData = yield client.query({
            query: GET_CONVERSATION,
            variables: {
                url,
            },
            fetchPolicy: 'network-only',
        });
        return Object.assign(Object.assign({}, responseData.data.me.conversation), { members: responseData.data.me.conversation.members.edges.map((mbr) => mbr.node), messages: responseData.data.me.conversation.messages.edges.map((msg) => msg.node) });
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const addChannel = createAsyncThunk('inbox/addChannel', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ name, description, icon = '', userIds = [], }, { rejectWithValue }) {
    try {
        const addChannelData = yield client.mutate({
            mutation: ADD_CHANNEL,
            variables: { name, description, iconUrl: icon, userIds },
        });
        return addChannelData.data.addChannel;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const removeChannel = createAsyncThunk('inbox/removeChannel', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ channelId }, { rejectWithValue }) {
    try {
        const removeChannelData = yield client.mutate({
            mutation: REMOVE_CHANNEL,
            variables: { channelId },
        });
        return { removeChannelData, channelId };
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const addDirectConversation = createAsyncThunk('inbox/addDirectConversation', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ title, id }, { rejectWithValue }) {
    try {
        const responseData = yield client.mutate({
            mutation: ADD_CONVERSATION,
            variables: { title, pageUrl: '', userIds: [id] },
        });
        return responseData.data.addConversation.conversation;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const addConversationMember = createAsyncThunk('inbox/addConversationMember', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ conversationId, userId }, { rejectWithValue }) {
    try {
        const response = yield client.mutate({
            mutation: ADD_CONVERSATION_MEMBER,
            variables: { conversationId, userId },
        });
        return response.data.addConversationMember.conversationmember;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const removeConversationMember = createAsyncThunk('inbox/removeConversationMember', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ conversationId, userId }, { rejectWithValue }) {
    try {
        const response = yield client.mutate({
            mutation: REMOVE_CONVERSATION_MEMBER,
            variables: { conversationId, userId },
        });
        return Object.assign({ conversationId, userId }, response.data.removeConversationMember);
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const addChatMessage = createAsyncThunk('inbox/addChatMessage', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ conversationId, image, text, mentions, }, { rejectWithValue }) {
    try {
        const wrappedMutation = withAttachments((options) => client.mutate(Object.assign({ mutation: ADD_CONVERSATION_MESSAGE }, options)));
        const response = yield wrappedMutation({
            variables: {
                conversationId,
                text: text || '',
                image: image || null,
                mentions: mentions !== null && mentions !== void 0 ? mentions : null,
            },
        });
        return Object.assign(Object.assign({}, response.data.sendMessage.message), { sendByMe: true });
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
const inboxSlice = createSlice({
    name: 'inbox',
    initialState,
    reducers: {
        resetInboxState(state) {
            state.channels = [];
            state.privateMessages = [];
            state.specificationAndFolderConversations = [];
            state.conversation = null;
            state.status = {
                fetchChannels: 'idle',
                fetchPrivateMessages: 'idle',
                fetchSpecificationAndFolderConversations: 'idle',
                fetchConversation: 'idle',
            };
            state.errors = {
                fetchChannels: null,
                fetchPrivateMessages: null,
                fetchSpecificationAndFolderConversations: null,
                fetchConversation: null,
            };
            state.refetchFlags = {
                fetchChannels: false,
                fetchPrivateMessages: false,
                fetchSpecificationAndFolderConversations: false,
                fetchConversation: false,
            };
            state.unreadCount = 0;
            state.unreadCountByConversation = {};
        },
        addWebSocketMessage: (state, action) => {
            // Add message received via WebSocket without making HTTP request
            if (state.conversation && state.conversation.messages) {
                const message = Object.assign(Object.assign({}, action.payload), { sendByMe: action.payload.sendByMe || false });
                const alreadyExists = state.conversation.messages.some((existing) => existing.id === message.id);
                if (!alreadyExists) {
                    state.conversation.messages = [...state.conversation.messages, message];
                }
            }
        },
        refreshInboxData: (state) => {
            state.refetchFlags.fetchChannels = true;
            state.refetchFlags.fetchPrivateMessages = true;
            state.refetchFlags.fetchSpecificationAndFolderConversations = true;
        },
        incrementUnreadCount: (state) => {
            state.unreadCount += 1;
        },
        incrementUnreadCountForConversation: (state, action) => {
            const { conversationId } = action.payload;
            const result = updateUnreadCountForConversation(conversationId, true);
            state.unreadCount = result.totalCount;
            state.unreadCountByConversation = result.unreadCounts;
        },
        clearUnreadCount: (state) => {
            clearUnreadCounts();
            state.unreadCount = 0;
            state.unreadCountByConversation = {};
        },
        clearUnreadCountForConversation: (state, action) => {
            const { conversationId } = action.payload;
            const result = updateUnreadCountForConversation(conversationId, false);
            state.unreadCount = result.totalCount;
            state.unreadCountByConversation = result.unreadCounts;
        },
        cleanupOrphanedUnreadCounts: (state) => {
            const currentConversationIds = new Set([
                ...state.channels.map((c) => { var _a; return (_a = c.conversation) === null || _a === void 0 ? void 0 : _a.id; }).filter(Boolean),
                ...state.privateMessages.map((c) => c.id),
                ...state.specificationAndFolderConversations.map((c) => c.id),
            ]);
            const currentUnreadCounts = state.unreadCountByConversation;
            const cleanedCounts = {};
            let newTotal = 0;
            Object.entries(currentUnreadCounts).forEach(([conversationId, count]) => {
                if (currentConversationIds.has(conversationId)) {
                    cleanedCounts[conversationId] = count;
                    newTotal += count;
                }
            });
            state.unreadCountByConversation = cleanedCounts;
            state.unreadCount = newTotal;
            saveUnreadCounts(cleanedCounts);
            saveTotalUnreadCount(newTotal);
        },
    },
    extraReducers: (builder) => {
        builder
            // fetchChannels
            .addCase(fetchChannels.pending, (state) => {
            state.status.fetchChannels = 'loading';
            state.refetchFlags.fetchChannels = false;
        })
            .addCase(fetchChannels.fulfilled, (state, action) => {
            state.status.fetchChannels = 'succeeded';
            state.channels = action.payload;
        })
            .addCase(fetchChannels.rejected, (state, action) => {
            state.status.fetchChannels = 'failed';
            state.errors.fetchChannels = action.payload || 'Failed to fetch channels';
        })
            // fetchPrivateConversations
            .addCase(fetchPrivateMessages.pending, (state) => {
            state.status.fetchPrivateMessages = 'loading';
            state.refetchFlags.fetchPrivateMessages = false;
        })
            .addCase(fetchPrivateMessages.fulfilled, (state, action) => {
            state.status.fetchPrivateMessages = 'succeeded';
            state.privateMessages = action.payload;
        })
            .addCase(fetchPrivateMessages.rejected, (state, action) => {
            state.status.fetchPrivateMessages = 'failed';
            state.errors.fetchPrivateMessages =
                action.payload || 'Failed to fetch private conversations';
        })
            // fetchSpecificationAndFolderConversations
            .addCase(fetchSpecificationAndFolderConversations.pending, (state) => {
            state.status.fetchSpecificationAndFolderConversations = 'loading';
            state.refetchFlags.fetchSpecificationAndFolderConversations = false;
        })
            .addCase(fetchSpecificationAndFolderConversations.fulfilled, (state, action) => {
            state.status.fetchSpecificationAndFolderConversations = 'succeeded';
            state.specificationAndFolderConversations = action.payload;
        })
            .addCase(fetchSpecificationAndFolderConversations.rejected, (state, action) => {
            state.status.fetchSpecificationAndFolderConversations = 'failed';
            state.errors.fetchSpecificationAndFolderConversations =
                action.payload || 'Failed to fetch specification and folder conversations';
        })
            // fetchConversation
            .addCase(fetchConversation.pending, (state) => {
            state.status.fetchConversation = 'loading';
            state.refetchFlags.fetchConversation = false;
        })
            .addCase(fetchConversation.fulfilled, (state, action) => {
            state.status.fetchConversation = 'succeeded';
            state.conversation = action.payload;
        })
            .addCase(fetchConversation.rejected, (state, action) => {
            state.status.fetchConversation = 'failed';
            state.errors.fetchConversation =
                action.payload || 'Failed to fetch conversation';
        })
            // addChannel
            .addCase(addChannel.pending, (state) => {
            state.status.addChannel = 'loading';
            state.refetchFlags.addChannel = false;
        })
            .addCase(addChannel.fulfilled, (state, action) => {
            state.status.addChannel = 'succeeded';
            state.channels = [...state.channels, action.payload];
        })
            .addCase(addChannel.rejected, (state, action) => {
            state.status.addChannel = 'failed';
            state.errors.addChannel = action.payload || 'Failed to add channel';
        })
            // removeChannel
            .addCase(removeChannel.pending, (state) => {
            state.status.removeChannel = 'loading';
            state.refetchFlags.removeChannel = false;
        })
            .addCase(removeChannel.fulfilled, (state, action) => {
            const { channelId } = action.payload;
            state.status.removeChannel = 'succeeded';
            state.channels = state.channels.filter((item) => item.id !== channelId);
        })
            .addCase(removeChannel.rejected, (state, action) => {
            state.status.removeChannel = 'failed';
            state.errors.removeChannel = action.payload || 'Failed to add channel';
        })
            // addDirectCOnversation
            .addCase(addDirectConversation.pending, (state) => {
            state.status.addDirectConversation = 'loading';
            state.refetchFlags.addDirectConversation = false;
        })
            .addCase(addDirectConversation.fulfilled, (state) => {
            state.status.addDirectConversation = 'succeeded';
            state.refetchFlags.fetchPrivateMessages = true;
        })
            .addCase(addDirectConversation.rejected, (state, action) => {
            state.status.addDirectConversation = 'failed';
            state.errors.addDirectConversation =
                action.payload || 'Failed to add direct conversation';
        })
            // addChatMessage
            .addCase(addChatMessage.pending, (state) => {
            state.status.addMessage = 'loading';
            state.refetchFlags.addMessage = false;
        })
            .addCase(addChatMessage.fulfilled, (state, action) => {
            state.status.addMessage = 'succeeded';
            const alreadyExists = state.conversation.messages.some((existing) => existing.id === action.payload.id);
            if (!alreadyExists) {
                state.conversation.messages = [...state.conversation.messages, action.payload];
            }
        })
            .addCase(addChatMessage.rejected, (state, action) => {
            state.status.addMessage = 'failed';
            state.errors.addMessage = action.payload || 'Failed to add chat message';
        })
            // addConversationMember
            .addCase(addConversationMember.pending, (state) => {
            state.status.addMember = 'loading';
        })
            .addCase(addConversationMember.fulfilled, (state, action) => {
            state.status.addMember = 'succeeded';
            if (state.conversation) {
                state.conversation.members = [...(state.conversation.members || []), action.payload];
            }
        })
            .addCase(addConversationMember.rejected, (state, action) => {
            state.status.addMember = 'failed';
            state.errors.addMember = action.payload || 'Failed to add conversation member';
        })
            // removeConversationMember
            .addCase(removeConversationMember.pending, (state) => {
            state.status.removeMember = 'loading';
        })
            .addCase(removeConversationMember.fulfilled, (state, action) => {
            state.status.removeMember = 'succeeded';
            if (state.conversation && action.payload.removed) {
                state.conversation.members = (state.conversation.members || []).filter((member) => { var _a; return ((_a = member.user) === null || _a === void 0 ? void 0 : _a.id) !== action.payload.userId; });
            }
        })
            .addCase(removeConversationMember.rejected, (state, action) => {
            state.status.removeMember = 'failed';
            state.errors.removeMember =
                action.payload || 'Failed to remove conversation member';
        })
            // fetchUserUnreadCounts
            .addCase(fetchUserUnreadCounts.pending, (state) => {
            state.status.fetchUserUnreadCounts = 'loading';
            state.errors.fetchUserUnreadCounts = null;
        })
            .addCase(fetchUserUnreadCounts.fulfilled, (state, action) => {
            state.status.fetchUserUnreadCounts = 'succeeded';
            const serverUnreadCounts = action.payload;
            const serverUnreadCountsMap = {};
            serverUnreadCounts.forEach((item) => {
                serverUnreadCountsMap[item.conversationId] = item.count;
            });
            const localUnreadCounts = state.unreadCountByConversation;
            const mergedCounts = Object.assign({}, localUnreadCounts);
            // Update with server counts (server is source of truth)
            Object.entries(serverUnreadCountsMap).forEach(([conversationId, count]) => {
                mergedCounts[conversationId] = count;
            });
            const totalCount = Object.values(mergedCounts).reduce((sum, count) => sum + count, 0);
            state.unreadCountByConversation = mergedCounts;
            state.unreadCount = totalCount;
            saveUnreadCounts(mergedCounts);
            saveTotalUnreadCount(totalCount);
        })
            .addCase(fetchUserUnreadCounts.rejected, (state, action) => {
            state.status.fetchUserUnreadCounts = 'failed';
            state.errors.fetchUserUnreadCounts =
                action.payload || 'Failed to fetch user unread counts';
        })
            // resetConversationUnreadCount
            .addCase(resetConversationUnreadCount.pending, (state) => {
            state.status.resetConversationUnreadCount = 'loading';
            state.errors.resetConversationUnreadCount = null;
        })
            .addCase(resetConversationUnreadCount.fulfilled, (state, action) => {
            state.status.resetConversationUnreadCount = 'succeeded';
            const { conversationId, success } = action.payload;
            if (success) {
                const result = updateUnreadCountForConversation(conversationId, false);
                state.unreadCount = result.totalCount;
                state.unreadCountByConversation = result.unreadCounts;
            }
        })
            .addCase(resetConversationUnreadCount.rejected, (state, action) => {
            state.status.resetConversationUnreadCount = 'failed';
            state.errors.resetConversationUnreadCount =
                action.payload || 'Failed to reset conversation unread count';
        });
    },
});
export const { resetInboxState, addWebSocketMessage, refreshInboxData, incrementUnreadCount, incrementUnreadCountForConversation, clearUnreadCount, clearUnreadCountForConversation, cleanupOrphanedUnreadCounts, } = inboxSlice.actions;
export const selectInboxStatus = (state) => state.inbox.status;
export const selectInboxErrors = (state) => state.inbox.errors;
export const selectInboxRefetchFlags = (state) => state.inbox.refetchFlags;
export const selectInbox = (state) => state.inbox;
export const selectInboxChannels = (state) => state.inbox.channels;
export const selectInboxPrivateMessages = (state) => state.inbox.privateMessages;
export const selectInboxSpecificationAndFolderConversations = (state) => state.inbox.specificationAndFolderConversations;
export const selectInboxUnreadCount = (state) => state.inbox.unreadCount;
export const selectInboxUnreadCountByConversation = (state) => state.inbox.unreadCountByConversation;
export const selectInboxConversation = (state) => state.inbox.conversation;
export const selectInboxConversationMembers = (state) => { var _a; return (_a = state.inbox.conversation) === null || _a === void 0 ? void 0 : _a.members; };
export default inboxSlice.reducer;
